<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { ClipboardList, Loader2, X } from 'lucide-svelte';

  import { customerList, listExcluded } from '$lib/api/lists';
  import CustomerListPanel from '$lib/components/CustomerListPanel.svelte';

  /**
   * The customer's list, reachable from the results.
   *
   * The button is here and the panel is CustomerListPanel, which the last step
   * of the guide shows too: the list is uploaded once and used from wherever
   * the customer happens to be.
   */

  let open = false;
  let container: HTMLElement | undefined;

  $: list = $customerList;
  $: working = list?.status === 'pending' || list?.status === 'matching';

  /**
   * Close on a click outside — decided by where the click happened, not by
   * where its target is by the time this runs.
   *
   * `container.contains(event.target)` is the usual form and it is wrong here.
   * Svelte flushes its updates before this window listener runs, so a control
   * inside the panel that replaces itself — "Code eingeben", which swaps the
   * link for the field — has already left the document, `contains` says no, and
   * the panel shuts on the customer at the moment they asked for the thing they
   * wanted.
   *
   * composedPath is captured when the event is dispatched, so it still names
   * the panel the click landed in.
   */
  function onWindowClick(event: MouseEvent) {
    if (!open || !container) return;
    if (!event.composedPath().includes(container)) open = false;
  }
</script>

<svelte:window on:click={onWindowClick} on:keydown={(e) => e.key === 'Escape' && (open = false)} />

<div class="relative" bind:this={container}>
  <button
    type="button"
    on:click={() => (open = !open)}
    title={$_('customerList.button')}
    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-control border transition-colors {list &&
    $listExcluded
      ? 'border-accent-300 bg-accent-50 text-accent-700'
      : 'chrome-control'}"
    aria-haspopup="dialog"
    aria-expanded={open}
  >
    {#if working}
      <Loader2 class="h-4 w-4 animate-spin" />
    {:else}
      <ClipboardList class="h-4 w-4" />
    {/if}
    <!--
      One name in both states, and it is the tooltip rather than a label.
      Whether the list is being subtracted is said by the button being lit, not
      by its wording changing under the customer's cursor.
    -->
    <span class="sr-only">{$_('customerList.button')}</span>
  </button>

  {#if open}
    <div
      class="absolute right-0 z-50 mt-1 w-80 rounded-surface border border-hairline bg-surface-panel p-4 shadow-overlay"
      role="dialog"
      aria-label={$_('customerList.title')}
    >
      <div class="mb-3 flex items-start justify-between gap-2">
        <h3 class="text-sm font-semibold text-gray-900">{$_('customerList.title')}</h3>
        <button
          type="button"
          class="text-gray-400 transition-colors hover:text-gray-600"
          on:click={() => (open = false)}
          aria-label={$_('buttons.close')}
        >
          <X class="h-4 w-4" />
        </button>
      </div>

      <CustomerListPanel on:done={() => (open = false)} />
    </div>
  {/if}
</div>
